import { quickFiltersCategories } from "@store/actions/quickFilters";
import { getAllTracks, TTrack } from "@store/actions/track";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ICatalogue, ICatalogueFilters } from "@/models/models";
import { catalogue, catalogueFilters } from "@/store/actions/catalogue";

export interface CatalogueState {
  catalogue: { data: ICatalogue[]; pageCount: number };
  tracks: { data: TTrack[]; pageCount: number; totalCount?: number };
  catalogueFilters: ICatalogueFilters;
  catalogSearchResults: ICatalogue[];
  loading: boolean;
  accumulatedTracks: TTrack[];
}

const InitialCatalogue: { data: ICatalogue[]; pageCount: number } = { data: [], pageCount: 0 };
const InitialTracks: { data: TTrack[]; pageCount: number } = { data: [], pageCount: 0 };
const InitialCatalogueFilters: ICatalogueFilters = {} as ICatalogueFilters;
const InitialSearchResults: ICatalogue[] = [];

const initialState: CatalogueState = {
  catalogue: InitialCatalogue,
  tracks: InitialTracks,
  catalogueFilters: InitialCatalogueFilters,
  catalogSearchResults: InitialSearchResults,
  loading: true,
  accumulatedTracks: [],
};

const { actions, reducer } = createSlice({
  name: "catalogue",
  initialState,
  reducers: {
    setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    resetTracks: (state) => {
      state.tracks = { data: [], pageCount: 0 };
      state.accumulatedTracks = [];
    },
    resetAccumulatedTracks: (state) => {
      state.accumulatedTracks = [];
    },
    restoreAccumulatedTracks: (state, { payload }: PayloadAction<TTrack[]>) => {
      state.accumulatedTracks = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(catalogue.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }

        state.catalogue = payload as { data: ICatalogue[]; pageCount: number };
        state.loading = false;
      })
      .addCase(catalogue.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllTracks.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }

        state.tracks = payload;
        // Accumulate: append new tracks, deduplicate by _id
        const existingIds = new Set(state.accumulatedTracks.map((t) => t._id));
        const newTracks = payload.data.filter((t) => !existingIds.has(t._id));
        state.accumulatedTracks = [...state.accumulatedTracks, ...newTracks];
        state.loading = false;
      })
      .addCase(getAllTracks.pending, (state) => {
        state.loading = true;
      })
      .addCase(catalogueFilters.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }

        state.catalogueFilters = payload as ICatalogueFilters;
      })
      .addCase(quickFiltersCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(quickFiltersCategories.fulfilled, (state) => {
        state.loading = false;
      });
  },
});

export const { setIsLoading, resetTracks, resetAccumulatedTracks, restoreAccumulatedTracks } = actions;
export default reducer;
